import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";
import { getData, postData, patchData, deleteData } from "../api/ApiServices";
import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import CustomAlert from "../view/common/alert/CustomAlert";
import { ApiResponse, CityResponse } from "../models/citytypes";
import { stateResponse } from "../models/statetypes";
import { CountryResponse } from "../models/countrytypes";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";

interface StateData {
  _id: string;
  stateName: string;
  gstCode: string;
  status: string;
}

interface CountryData {
  _id: string;
  countryName: string;
  countryCode: string;
  status: string;
}

interface CityData {
  _id: string;
  cityName: string;
  stateId: StateData;
  countryId: CountryData;
  status: string;
  createdAt: string;
  updatedAt: string;
}


interface City {
  _id: string;
  cityName: string;
  stateName: string;
  countryName: string;
  status: string;
}

const customStyles = `
  .form-group {
    position: relative;
    margin-bottom: 20px;
    width: 100%;
  }
  
  .form-group input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  
  .form-group input.input-error {
    border-color: #ff6b6b;
  }
  
  .form-error-message {
    color: #ff6b6b;
    font-size: 12px;
    margin-top: 4px;
    position: absolute;
    bottom: -18px;
    left: 0;
  }
  
  .select-container {
    margin-bottom: 20px;
    position: relative;
    z-index: 2;
  }
  
  .select-error .react-select__control {
    border-color: #ff6b6b;
  }
  
  /* Improved modal styles with reduced size */
  .item-models-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .item-models-modal {
    background-color: #fff;
    border-radius: 8px;
    width: 80%;
    max-width: 400px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }
  
  .item-models-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 7px 10px;
    border-bottom: 1px solid #eee;
  }
  
  .item-models-modal-header h5 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
  
  .item-models-modal-close {
    background: none;
    border: none;
    cursor: pointer;
    padding:3px;
  }
  
  .modal-form-container {
    padding: 15px;
    overflow-y: auto;
    max-height: calc(80vh - 50px); /* Reduced height minus header */
  }
  
  /* Fix for react-select dropdown positioning */
  .react-select__menu {
    z-index: 10;
  }
  
  /* Submit button styles */
  .submit-button {
    width: 100%;
    padding: 8px;
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    margin-top: 10px;
  }
  
  .submit-button:hover {
    background-color: #45a049;
  }
`;

const CityMaster: React.FC = () => {
  const [cityData, setCityData] = useState<City[]>([]);
  const [stateOptions, setStateOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [countryOptions, setCountryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [newCity, setNewCity] = useState({
    cityName: "",
    stateId: "",
    countryId: "",
    status: "ACTIVE",
  });
  const [originalCity, setOriginalCity] = useState({
    cityName: "",
    stateId: "",
    countryId: "",
    status: "ACTIVE",
  });
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [validationErrors, setValidationErrors] = useState<{
    cityName?: string;
    stateId?: string;
    countryId?: string;
    status?: string;
  }>({});

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("cityName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => {}
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => {}
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const statusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const permission = useSelector((state: RootState) => state.permissions);
    
  const [initializeCityMaster, setIsCityMasterInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    setIsCityMasterInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeCityMaster) {
      fetchCities();
      fetchStates();
      fetchCountries();
    }
  }, [
    initializeCityMaster,
    searchTerm,
    currentPage,
    itemsPerPage,
    filters,
    sortBy,
    sortOrder,
  ]);

  const fetchCities = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData<ApiResponse<CityResponse>>(
        "/citymasters",
        {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          filter: filters,
          sortBy: sortBy,
          sortOrder: sortOrder,
        }
      );

      const transformedData = response.data.data.map((city: any) => {
        // console.log(city)
        return {
          _id: city._id,
          cityName: city.cityName,
          stateName: city.stateId?.stateName || 'N/A',
          countryName: city.countryId?.countryName || 'N/A',
          status: city.status,
          stateId: city.stateId?._id || '',
          countryId: city.countryId?._id || '',
        };
      });

      setCityData(transformedData);
      setTotalPages(response?.data?.pagination?.totalPages || 1);
      setTotalItems(response?.data?.pagination?.total || 0);
    } catch (error) {
      console.error("Error fetching cities:", error);
      configureAlert(
        "Failed to load cities data.",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStates = async () => {
  try {
      const response = await getData<ApiResponse<stateResponse>>(
        "/statemaster",
        {
          filter: { status: "ACTIVE" }
        }
      );
      const formattedOptions =
        response?.data?.data.map((s) => ({
          label: s.stateName,
          value: s._id || "",
        })) || [];
      setStateOptions(formattedOptions);
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };


  const fetchCountries = async () => {
    try {
      const response = await getData<ApiResponse<CountryResponse>>(
        "/country-masters",
        {
          filter: { status: "ACTIVE" }
        }
      );
      const formattedOptions =
        response?.data?.data.map((c) => ({
          label: c.countryName,
          value: c._id || "",
        })) || [];
      setCountryOptions(formattedOptions);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {},
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

 
  const validateForm = (): boolean => {
    const errors: {
      cityName?: string;
      stateId?: string;
      countryId?: string;
      status?: string;
    } = {};

    if (!newCity.cityName.trim()) {
      errors.cityName = "City Name is required";
    }

    if (!newCity.stateId) {
      errors.stateId = "State is required";
    }

    if (!newCity.countryId) {
      errors.countryId = "Country is required";
    }

    if (editingCity && !newCity.status) {
      errors.status = "Status is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };




  const hasChanges = (): boolean => {
    return (
      newCity.cityName !== originalCity.cityName ||
      newCity.stateId !== originalCity.stateId ||
      newCity.countryId !== originalCity.countryId ||
      newCity.status !== originalCity.status
    );
  };

  const handleAddOrEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (editingCity) {
      if (!hasChanges()) {
        configureAlert(
          "No changes were made.",
          closeAlert,
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
        return;
      }
    }

    try {
      let updatedCity = { ...newCity };

      if (editingCity) {
        if (editingCity.status === "INACTIVE" && newCity.status === "ACTIVE") {
          updatedCity.status = "ACTIVE";
        }

        await patchData(`/citymasters`, editingCity._id, updatedCity);
        setIsModalOpen(false);

        configureAlert(
          `City "${newCity.cityName}" has been updated successfully.`,
          () => {
            fetchCities();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        const response : any= await postData("/citymasters", updatedCity);
        setIsModalOpen(false);

        if(response.statusCode === 1114){

          configureAlert(
            `City "${newCity.cityName}" has been added successfully.`,
            () => {
            fetchCities();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      }else{
        configureAlert(
        "Duplicate Entry!",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      }
      }

      setEditingCity(null);
      setNewCity({
        cityName: "",
        stateId: "",
        countryId: "",
        status: "ACTIVE",
      });
      setOriginalCity({
        cityName: "",
        stateId: "",
        countryId: "",
        status: "ACTIVE",
      });
    } catch (error) {
      console.error("Error saving city:", error);
      configureAlert(
        "Duplicate Entry!",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    }
  };

  const handleDeleteCity = async (id: string) => {
    const cityToDelete = cityData.find((city) => city._id === id);

    if (cityToDelete) {
      configureAlert(
        `Are you sure you want to Inactive "${cityToDelete.cityName}"?`,
        async () => {
          closeAlert();

          try {
            await deleteData("/citymasters", id);

            configureAlert(
              "City has been Inactive successfully.",
              () => {
                fetchCities();
                closeAlert();
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          } catch (error) {
            console.error("Error deleting city:", error);
            configureAlert(
              "Failed to delete city.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewCity((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (selected: any, { name }: any) => {
    setNewCity((prev) => ({
      ...prev,
      [name]: selected?.value || "",
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleModalClose = () => {
    if (isModalOpen && editingCity && hasChanges()) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          setEditingCity(null);
          setNewCity({
            cityName: "",
            stateId: "",
            countryId: "",
            status: "ACTIVE",
          });
          setOriginalCity({
            cityName: "",
            stateId: "",
            countryId: "",
            status: "ACTIVE",
          });
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else if (
      isModalOpen &&
      !editingCity &&
      (newCity.cityName.trim() !== "" ||
        newCity.stateId !== "" ||
        newCity.countryId !== "")
    ) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          setEditingCity(null);
          setNewCity({
            cityName: "",
            stateId: "",
            countryId: "",
            status: "ACTIVE",
          });
          setOriginalCity({
            cityName: "",
            stateId: "",
            countryId: "",
            status: "ACTIVE",
          });
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsModalOpen(false);
      setEditingCity(null);
      setNewCity({
        cityName: "",
        stateId: "",
        countryId: "",
        status: "ACTIVE",
      });
      setOriginalCity({
        cityName: "",
        stateId: "",
        countryId: "",
        status: "ACTIVE",
      });
    }
  };

  const handleEditCity = (city: City) => {
    const stateId = stateOptions.find((s) => s.label === city.stateName)?.value || "";
    const countryId = countryOptions.find((c) => c.label === city.countryName)?.value || "";

    const cityData = {
      cityName: city.cityName,
      stateId,
      countryId,
      status: city.status,
    };

    setNewCity(cityData);
    setOriginalCity(cityData);
    setEditingCity(city);
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };
 
 
  const columns = [
    { key: "cityName", label: "City Name", sortable: true },
    {
      key: "stateName",
      label: "State Name",
      sortable: true,
    },
    {
      key: "countryName",
      label: "Country Name",
      sortable: true,
    },
    { key: "status", label: "Status", sortable: true },
  ];

  return (
    <div className="item-models-container">
      <style>{customStyles}</style>

      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>General</span>
              <span>City Master</span>
            </div>
            {permission.menus.citymasters.create ?
            <div
              className="items-button-container tooltip-wrapper"
              onClick={() => {
                setNewCity({
                  cityName: "",
                  stateId: "",
                  countryId: "",
                  status: "ACTIVE",
                });
                setOriginalCity({
                  cityName: "",
                  stateId: "",
                  countryId: "",
                  status: "ACTIVE",
                });
                setEditingCity(null);
                setValidationErrors({});
                setIsModalOpen(true);
              }}
            >
              <button className="item-models-add-btn">
                <Plus size={16} />
              </button>
            </div>
            :<></>}
          </div>
        </div>
      </div>

      <div className="box-container">
        {isLoading ? (
          <div className="spinner"></div>
        ) : (
          <TableComponent
            columns={columns}
            data={cityData}
            onEdit={handleEditCity}
            onDelete={handleDeleteCity}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            context="cityMaster"
          />
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>

      {isModalOpen && (
        <div className="item-models-modal-overlay">
          <div className="item-models-modal">
            <div className="item-models-modal-header">
              <h5>{editingCity ? "Edit City" : "Add New City"}</h5>
              <button
                className="item-models-modal-close"
                onClick={handleModalClose}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-form-container">
              <form onSubmit={handleAddOrEdit} noValidate>
                <div className="form-group">
                  <input
                    type="text"
                    name="cityName"
                    placeholder="City Name"
                    value={newCity.cityName}
                    onChange={handleInputChange}
                    className={validationErrors.cityName ? "input-error" : ""}
                    required
                  />
                  {validationErrors.cityName && (
                    <div className="form-error-message">
                      {validationErrors.cityName}
                    </div>
                  )}
                </div>

                <div className="select-container">
                  <Select
                    name="stateId"
                    options={stateOptions}
                    value={stateOptions.find(
                      (s) => s.value === newCity.stateId
                    )}
                    onChange={(selected, action) =>
                      handleSelectChange(selected, action)
                    }
                    placeholder="Select State"
                    isSearchable
                    className={validationErrors.stateId ? "select-error" : ""}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {validationErrors.stateId && (
                    <div className="form-error-message">
                      {validationErrors.stateId}
                    </div>
                  )}
                </div>

                <div className="select-container">
                  <Select
                    name="countryId"
                    options={countryOptions}
                    value={countryOptions.find(
                      (c) => c.value === newCity.countryId
                    )}
                    onChange={(selected, action) =>
                      handleSelectChange(selected, action)
                    }
                    placeholder="Select Country"
                    isSearchable
                    className={validationErrors.countryId ? "select-error" : ""}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {validationErrors.countryId && (
                    <div className="form-error-message">
                      {validationErrors.countryId}
                    </div>
                  )}
                </div>
                {editingCity && (
                  <div className="select-container">
                    <Select
                      name="status"
                      options={statusOptions}
                      value={statusOptions.find(
                        (option) => option.value === newCity.status
                      )}
                      onChange={(selected, action) =>
                        handleSelectChange(selected, action)
                      }
                      placeholder="Select Status"
                      className={validationErrors.status ? "select-error" : ""}
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                    {validationErrors.status && (
                      <div className="form-error-message">
                        {validationErrors.status}
                      </div>
                    )}
                  </div>
                )}

                <button type="submit" className="submit-button">
                  {isLoading ? "Saving..." : editingCity ? "Update" : "Add"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default CityMaster;
